(function(){
  // Find section: prefer original id pattern, fallback to old id
  const root =
    document.querySelector('.st-wheels-gallery[id^="stwg-section-"]') ||
    document.getElementById('stw-gallery');
  if (!root) return;

  // ---- CONFIG ----
  const endpoint = '/api/2024-10/graphql.json';
  const token =
    (root.dataset.sfToken && root.dataset.sfToken.trim()) ||
    (document.querySelector('meta[name="metafield_storefront_public_token"]')?.content?.trim()) ||
    '';
  if (!token) { console.error('[STW] Storefront token missing'); return; }

  const TYPE = root.dataset.type || 'vehicle_gallery';
  const PAGE_SIZE = parseInt(root.dataset.pageSize || '60', 10);
  const routePrefix = root.dataset.routePrefix || '/';
  const SCROLL_THRESHOLD = 320; // px from bottom to trigger auto-load
  const FILL_VIEWPORT_ITERATIONS_MAX = 6; // safety for very tall screens

  // ---- DOM (match original hooks) ----
  const grid = document.getElementById('gallery-results'); // original id from your markup
  const selMake  = document.getElementById('stw-make');
  const selModel = document.getElementById('stw-model');
  const selWheel = document.getElementById('stw-wheel');
  const btnClear = document.getElementById('stw-clear');

  // ---- STATE ----
  const STATE = {
    items: [],
    filtered: [],
    rendered: 0,
    batch: 24,
    loading: false,           // network fetching (cursor pages)
    rendering: false,         // guard render bursts
    pager: { hasNextPage: true, endCursor: null },
    options: { makes: new Set(), modelsByMake: {}, wheels: new Set() }
  };

  // ---- GraphQL ----
  const QUERY = `
    query VehicleGalleries($first: Int = 20, $after: String) {
      metaobjects(type: "vehicle_gallery", first: $first, after: $after) {
        edges {
          cursor
          node {
            id
            type
            handle
            fields {
              key
              value
              reference {
                __typename
                ... on Metaobject { id type handle fields { key value } }
                ... on MediaImage { image { url altText width height } }
                ... on GenericFile { url }
              }
            }
          }
        }
        pageInfo { hasNextPage endCursor }
      }
    }`;

  async function gql(query, variables) {
    const r = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({ query, variables })
    });
    const json = await r.json().catch(()=>null);
    if (!json) return null;
    if (json.errors) console.error('[STW] GraphQL errors', json.errors);
    return json.data || null;
  }

  // ---- Helpers ----
  const normalize = s => (s||'').toString().trim();
  const fv = (fields, key) => fields.find(x => x.key === key)?.value || '';
  const fr = (fields, key) => fields.find(x => x.key === key)?.reference || null;

  // Fisher–Yates shuffle (in place)
  function shuffleArray(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  function toItem(node) {
    const fields = node.fields || [];
    const brand = normalize(fv(fields, 'brand'));     // text field
    const model = normalize(fv(fields, 'model'));     // text field

    // wheel_model → Metaobject ref (title/name)
    const wheelRef = fr(fields, 'wheel_model');
    const wheel = normalize(
      wheelRef?.fields?.find(f => f.key === 'title')?.value
      || wheelRef?.fields?.find(f => f.key === 'name')?.value
      || ''
    );

    const title = normalize(fv(fields, 'title')) || [brand, model].filter(Boolean).join(' ');
    const heroRef = fr(fields, 'hero_image');
    let hero = '', alt = title;
    if (heroRef?.__typename === 'MediaImage' && heroRef.image?.url) {
      hero = heroRef.image.url; alt = heroRef.image.altText || title;
    } else if (heroRef?.__typename === 'GenericFile' && heroRef.url) {
      hero = heroRef.url;
    }

    const url = (routePrefix || '/') + node.handle;
    return { id: node.id, handle: node.handle, url, brand, model, wheel, title, hero, alt };
  }

  async function loadAll() {
    if (STATE.loading) return;
    STATE.loading = true;

    let data = await gql(QUERY, { first: PAGE_SIZE, after: null });
    consume(data);             // push items from first page + shuffle

    while (STATE.pager.hasNextPage) {
      data = await gql(QUERY, { first: PAGE_SIZE, after: STATE.pager.endCursor });
      consume(data);           // push items from next page + shuffle
    }

    STATE.loading = false;
    if (!STATE.items.length) showEmpty('No entries found.');
  }

  function consume(data) {
    const edges = data?.metaobjects?.edges || [];
    edges.forEach(({ node }) => {
      const it = toItem(node);
      STATE.items.push(it);
      if (it.brand) STATE.options.makes.add(it.brand);
      if (it.wheel) STATE.options.wheels.add(it.wheel);
      if (it.brand && it.model) {
        (STATE.options.modelsByMake[it.brand] ||= new Set()).add(it.model);
      }
    });
    STATE.pager = data?.metaobjects?.pageInfo || { hasNextPage:false, endCursor:null };

    // 🔀 Shuffle the master list after each fetched page
    shuffleArray(STATE.items);
  }

  // ---- Filters UI ----
  function setSelectOptions(select, values, firstLabel){
    const prev = normalize(select.value);
    select.innerHTML = '';
    const opt0 = document.createElement('option');
    opt0.value = ''; opt0.textContent = firstLabel;
    select.appendChild(opt0);

    const list = Array.isArray(values) ? values : Array.from(values);
    list.sort((a,b)=>a.localeCompare(b)).forEach(v=>{
      const o = document.createElement('option'); o.value=v; o.textContent=v;
      select.appendChild(o);
    });
    if (prev && list.includes(prev)) select.value = prev;
  }

  function hydrateFilterUIFromData() {
    // Make = all makes
    setSelectOptions(selMake,  STATE.options.makes,  'Make');

    // Wheel = always enabled & global list (independent)
    selWheel?.removeAttribute('disabled');
    setSelectOptions(selWheel, STATE.options.wheels, 'All Wheels');

    // Model depends on Make (unchanged)
    const mk = normalize(selMake?.value);
    if (mk && STATE.options.modelsByMake[mk]) {
      selModel?.removeAttribute('disabled');
      setSelectOptions(selModel, Array.from(STATE.options.modelsByMake[mk]), 'All Models');
    } else if (selModel) {
      selModel.setAttribute('disabled','disabled');
      selModel.innerHTML = '<option value="">Model</option>';
    }
  }

  // URL params ↔ UI
  function readParams() {
    const u = new URL(location.href);
    return {
      make:  normalize(u.searchParams.get('make')),
      model: normalize(u.searchParams.get('model')),
      wheel: normalize(u.searchParams.get('wheel'))
    };
  }
  function writeParams(p){
    const u = new URL(location.href);
    const s = u.searchParams;
    p.make ? s.set('make', p.make) : s.delete('make');
    p.model ? s.set('model', p.model) : s.delete('model');
    p.wheel ? s.set('wheel', p.wheel) : s.delete('wheel');
    history.replaceState({}, '', u.pathname + (s.toString() ? '?' + s.toString() : ''));
  }

  function applyParamsToUI() {
    const p = readParams();
    if (p.make && selMake) selMake.value = p.make;

    const mk = normalize(selMake?.value);
    if (mk && STATE.options.modelsByMake[mk]) {
      selModel?.removeAttribute('disabled');
      setSelectOptions(selModel, Array.from(STATE.options.modelsByMake[mk]), 'All Models');
      if (p.model && selModel) selModel.value = p.model;
    } else if (selModel) {
      selModel.setAttribute('disabled','disabled');
      selModel.innerHTML = '<option value="">Model</option>';
    }

    if (p.wheel && selWheel) selWheel.value = p.wheel;
  }

  // ---- Filtering ----
  function computeFiltered() {
    const mk = normalize(selMake?.value);
    const md = normalize(selModel?.value);
    const wh = normalize(selWheel?.value);
    STATE.filtered = STATE.items.filter(it => {
      if (mk && it.brand !== mk) return false;
      if (md && it.model !== md) return false;
      if (wh && it.wheel !== wh) return false;
      return true;
    });
  }

  // ---- Rendering ----
  function cardHTML(it, eager) {
    const title = it.title || [it.brand, it.model].filter(Boolean).join(' ');
    const img = it.hero;
    const alt = it.alt || title;
    const url = it.url;

    if (!img) {
      return `
        <div class="st-wheels-gallery-title stw-card">
          <div class="content">
            <a class="st-wheels-gallery__link" href="${url}">
              <div class="stwg-card__media"><div class="stw-card__ph"></div></div>
            </a>
            <h3><a href="${url}">${escapeHTML(title)}</a><div class="fader"></div></h3>
            ${it.wheel ? `<p>${escapeHTML(it.wheel)}</p>` : ''}
          </div>
        </div>`;
    }

    // responsive widths (Shopify CDN width param)
    const sep = img.includes('?') ? '&' : '?';
    const w480  = `${img}${sep}width=480`;
    const w640  = `${img}${sep}width=640`;
    const w840  = `${img}${sep}width=840`;
    const w1240 = `${img}${sep}width=1240`;

    return `
      <div class="st-wheels-gallery-title stw-card">
        <div class="content">
          <a class="st-wheels-gallery__link" href="${url}">
            <div class="stwg-card__media">
              <img
                ${eager ? 'loading="eager" fetchpriority="high"' : 'loading="lazy"'}
                decoding="async"
                src="${w840}"
                srcset="${w480} 480w, ${w640} 640w, ${w840} 840w, ${w1240} 1240w"
                sizes="(min-width: 990px) 33vw, 100vw"
                alt="${escapeHTML(alt)}"
              />
            </div>
          </a>
          <h3><a href="${url}">${escapeHTML(title)}</a><div class="fader"></div></h3>
          ${it.wheel ? `<p>${escapeHTML(it.wheel)}</p>` : ''}
        </div>
      </div>`;
  }

  function render(reset=false) {
    if (!grid) return;
    if (reset) { grid.innerHTML = ''; STATE.rendered = 0; }
    const end = Math.min(STATE.rendered + STATE.batch, STATE.filtered.length);
    if (end <= STATE.rendered) return; // nothing to add
    const slice = STATE.filtered.slice(STATE.rendered, end);
    const html = slice.map((it, idx) => cardHTML(it, STATE.rendered === 0 && idx === 0)).join('');
    grid.insertAdjacentHTML('beforeend', html);
    STATE.rendered = end;
  }

  function showEmpty(msg){
    if (!grid) return;
    grid.innerHTML = `<p>${escapeHTML(msg || 'No entries found.')}</p>`;
  }

  function escapeHTML(s) {
    return (s||'')
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
      .replace(/"/g,'&quot;').replace(/'/g,'&#39;');
  }

  // ---- Infinite Scroll ----
  function nearBottom() {
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;
    const docH = Math.max(
      document.body.scrollHeight, document.documentElement.scrollHeight,
      document.body.offsetHeight, document.documentElement.offsetHeight,
      document.body.clientHeight, document.documentElement.clientHeight
    );
    return (scrollY + viewportH + SCROLL_THRESHOLD) >= docH;
  }

  function maybeAutoLoad() {
    if (STATE.rendering) return;
    if (STATE.rendered >= STATE.filtered.length) return; // all done
    if (!nearBottom()) return;

    STATE.rendering = true;
    setTimeout(() => {
      render(false);
      STATE.rendering = false;

      // If tall viewport, keep filling a few iterations
      let guard = 0;
      while (STATE.rendered < STATE.filtered.length && nearBottom() && guard < FILL_VIEWPORT_ITERATIONS_MAX) {
        render(false);
        guard++;
      }
    }, 0);
  }

  window.addEventListener('scroll', maybeAutoLoad, { passive: true });
  window.addEventListener('resize', maybeAutoLoad);

  // ---- Events ----
  function onMakeChange() {
    const mk = normalize(selMake?.value);
    if (mk && STATE.options.modelsByMake[mk]) {
      selModel?.removeAttribute('disabled');
      setSelectOptions(selModel, Array.from(STATE.options.modelsByMake[mk]), 'All Models');
    } else if (selModel) {
      selModel.setAttribute('disabled','disabled');
      selModel.innerHTML = '<option value="">Model</option>';
    }
    writeParams({ make: selMake?.value || '', model: selModel?.value || '', wheel: selWheel?.value || '' });
    computeFiltered(); render(true); maybeAutoLoad();
  }
  function onModelChange() {
    writeParams({ make: selMake?.value || '', model: selModel?.value || '', wheel: selWheel?.value || '' });
    computeFiltered(); render(true); maybeAutoLoad();
  }
  function onWheelChange() {
    writeParams({ make: selMake?.value || '', model: selModel?.value || '', wheel: selWheel?.value || '' });
    computeFiltered(); render(true); maybeAutoLoad();
  }
  function onClear() {
    if (selMake) selMake.value = '';
    if (selWheel) selWheel.value = '';
    if (selModel) {
      selModel.setAttribute('disabled','disabled');
      selModel.innerHTML = '<option value="">Model</option>';
    }
    writeParams({ make:'', model:'', wheel:'' });
    computeFiltered(); render(true); maybeAutoLoad();
  }

  selMake?.addEventListener('change', onMakeChange);
  selModel?.addEventListener('change', onModelChange);
  selWheel?.addEventListener('change', onWheelChange);
  btnClear?.addEventListener('click', onClear);

  // ---- Bootstrap ----
  (async function init(){
    await loadAll();                // fetch & shuffle as pages arrive
    if (!STATE.items.length) return;

    hydrateFilterUIFromData();
    applyParamsToUI();
    computeFiltered();
    render(true);
    // Fill initial viewport if needed
    maybeAutoLoad();
  })();
})();
